import { useEffect, useState } from 'react';

import type { AssistanceService } from '../services/assistance-service/types';

/**
 * Hook to fetch and map knowledge source icons and friendly names from the agent knowledge configuration API.
 * Returns records mapping source identifiers to their icon URLs and friendly names, plus a loading state.
 * Falls back gracefully to empty maps if API call fails.
 *
 * @param assistanceService The assistance service instance to use for fetching configuration
 * @returns Object containing iconMap, friendlyNameMap, and isLoading state
 */
export const useKnowledgeSourceIconMap = (
	assistanceService: AssistanceService,
): {
	iconMap: Record<string, string>;
	friendlyNameMap: Record<string, string>;
	isLoading: boolean;
} => {
	const [knowledgeSourceIconMap, setKnowledgeSourceIconMap] = useState<Record<string, string>>({});
	const [knowledgeSourceFriendlyNameMap, setKnowledgeSourceFriendlyNameMap] = useState<
		Record<string, string>
	>({});
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		const fetchKnowledgeConfiguration = async () => {
			setIsLoading(true);
			try {
				const response = await assistanceService.getAgentKnowledgeConfiguration();
				const iconMap: Record<string, string> = {};
				const friendlyNameMap: Record<string, string> = {};

				response.knowledgeSources.forEach((config) => {
					iconMap[config.source] = config.iconUrl;
					friendlyNameMap[config.source] = config.friendlyName;
				});

				setKnowledgeSourceIconMap(iconMap);
				setKnowledgeSourceFriendlyNameMap(friendlyNameMap);
			} catch {
				// Fallback to local icons if API fails
			} finally {
				setIsLoading(false);
			}
		};

		fetchKnowledgeConfiguration();
	}, [assistanceService]);

	return {
		iconMap: knowledgeSourceIconMap,
		friendlyNameMap: knowledgeSourceFriendlyNameMap,
		isLoading,
	};
};
